import React, { useEffect, useId, useRef, useState } from "react";
import { Input } from "reactstrap";
import { getApi } from "../../helpers/auth_helpers";
import { mapAssociationToCustomer } from "../../pages/orders/orderParty";
import { partyPhoneToShareFields } from "./catalogueShareCountries";

export type SharePartyPick = {
  name: string;
  phone: string;
  email: string;
  countryIso: string;
};

export type PartyHit = {
  id: string;
  name: string;
  phone: string;
  email: string;
  /** Marked as reseller in Parties (reseller sharing). */
  isReseller?: boolean;
  forwardLimit?: number;
};

const usablePhone = (raw: string) => {
  const digits = raw.replace(/\D/g, "");
  if (/org:/i.test(raw) || digits.length < 6 || digits.length > 15) return "";
  return raw;
};

const toHit = (row: Record<string, unknown>): PartyHit | null => {
  const option = mapAssociationToCustomer(row as never);
  if (!option) return null;
  const phone = usablePhone(String(row.phoneNumber || option.customerPhone || "").trim());
  return {
    id: option.partyOrganizationId,
    name: option.customerName,
    phone,
    email: option.customerEmail,
    isReseller: row.isReseller === true,
    forwardLimit: Number(row.resellerForwardLimit) || undefined,
  };
};

export const searchParties = async (q: string): Promise<PartyHit[]> => {
  const params = new URLSearchParams({ offset: "0", limit: "8", search: q });
  const response: any = await getApi(
    `${process.env.REACT_APP_SERVERURL || ""}/organization/associations?${params}`
  );
  const docs = response?.data?.docs;
  if (response?.success === false || !Array.isArray(docs)) {
    throw new Error("Parties unavailable");
  }
  return docs.map((row: Record<string, unknown>) => toHit(row)).filter(Boolean) as PartyHit[];
};

const ShareRecipientNameField = ({
  id,
  value,
  onChange,
  onPick,
}: {
  id: string;
  value: string;
  onChange: (name: string) => void;
  onPick: (party: SharePartyPick) => void;
}) => {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [hits, setHits] = useState<PartyHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const generation = useRef(0);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const request = generation.current + 1;
    generation.current = request;
    const timer = window.setTimeout(() => {
      setLoading(true);
      setFailed(false);
      searchParties(value.trim())
        .then((rows) => {
          if (request === generation.current) setHits(rows);
        })
        .catch(() => {
          if (request === generation.current) {
            setHits([]);
            setFailed(true);
          }
        })
        .finally(() => {
          if (request === generation.current) setLoading(false);
        });
    }, value.trim() ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [open, value]);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const choose = (party: PartyHit) => {
    const fields = partyPhoneToShareFields(party.phone);
    onPick({
      name: party.name,
      phone: fields.phone,
      email: party.email,
      countryIso: party.phone ? fields.countryIso : "IN",
    });
    setOpen(false);
  };

  return (
    <div className="share-party-name" ref={box}>
      <Input
        id={id}
        type="text"
        value={value}
        autoComplete="off"
        placeholder="Search a party or type a name"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listId}
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          onChange(event.target.value);
          setOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") setOpen(false);
        }}
      />
      {open ? (
        <div className="share-party-name__list" id={listId} role="listbox" aria-label="Parties">
          {hits.map((party) => (
            <button
              type="button"
              key={party.id}
              role="option"
              className="share-party-name__option"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(party)}
            >
              <strong>{party.name}</strong>
              <small>{party.phone || "No mobile on this party"}</small>
            </button>
          ))}
          {loading ? <p role="status">Searching parties…</p> : null}
          {failed ? <p role="alert">Parties could not be loaded.</p> : null}
          {!loading && !failed && hits.length === 0 ? (
            <p>No matching party. Sharing with a new name and number adds one.</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default ShareRecipientNameField;
